/**
 * Selective sessionStorage cleanup for forced logout / auth failure paths.
 *
 * Do NOT use sessionStorage.clear() here — that wiped non-auth tab state
 * (instrumentation counters, contextual help return, search UI flags, drafts).
 *
 * Cleared on intentional logout / auth fallback:
 * - CSRF caches: `api_csrf_token`, legacy `csrf_token`
 * - Org invite stash: `org_invitation_token` — cleared for security so a later
 *   user on the same tab cannot redeem a stashed invite. Invite mid-flow only
 *   survives when the user does not log out / force-clear auth.
 * - Tab analytics session id: `app_session_id`
 * - Provider registration status cache: `provider_registration_status_ok:*`
 * - Provider setup checklist session: `provider_setup_checklist_session*`
 *
 * Preserved: search/dashboard instrumentation, contextual help, help feedback,
 * and other non-auth UX session keys.
 */
import { clearSessionId } from "../lib/sessionClear";
import { clearCachedCsrfToken } from "./csrfToken";
import { clearStashedOrgInviteToken } from "./orgInviteTokenSession";

/** Legacy client-generated CSRF key from `security.ts` / errorHandler. */
const LEGACY_CSRF_SESSION_KEY = "csrf_token";

const AUTH_SESSION_KEY_PREFIXES = [
  "provider_registration_status_ok:",
  "provider_setup_checklist_session",
] as const;

function removeSessionKeysByPrefix(prefixes: readonly string[]): void {
  if (typeof sessionStorage === "undefined") return;
  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => sessionStorage.removeItem(key));
}

export function clearAuthSessionStorage(): void {
  try {
    clearCachedCsrfToken();
    sessionStorage.removeItem(LEGACY_CSRF_SESSION_KEY);
    clearStashedOrgInviteToken();
    clearSessionId();
    removeSessionKeysByPrefix(AUTH_SESSION_KEY_PREFIXES);
  } catch {
    // sessionStorage may be unavailable (SSR / private mode)
  }
}
